/**
 * cookiesContent.js — Onglet « Cookies » (6 langues), même format que privacyContent.js.
 * Rendu par le même moteur de blocs dans LegalPages.jsx. Placeholders : {{name}} {{email}} {{dpo}} {{address}}.
 *
 * Ce texte décrit ce que le code fait réellement : AUCUN cookie de suivi ; stockage local du navigateur
 * pour des fonctions essentielles ; mesure d'usage interne (envoyée à nos seuls serveurs, refusable).
 */
const p       = (text)             => ({ t: "p", text });
const ul      = (items)            => ({ t: "ul", items });
const table   = (head, rows)       => ({ t: "table", head, rows });
const box     = (type, icon, text) => ({ t: "box", type, icon, text });
const contact = (items)            => ({ t: "contact", items });

export const COOKIES = {

/* ═══ FR ═══ */
fr: {
  toc: "Table des matières",
  summaryTitle: "L'essentiel",
  summary: [
    "Aucun cookie de suivi, aucun cookie publicitaire.",
    "Nous utilisons uniquement le stockage local de votre navigateur pour des fonctions essentielles (connexion, langue, thème, choix de confidentialité).",
    "Aucune donnée n'est vendue ni partagée avec des courtiers en données ou des réseaux publicitaires.",
    "Dans l'application mobile, les affichages web servent uniquement à des fonctions essentielles : aucun cookie de suivi n'y est collecté.",
  ],
  sections: [
    { title: "Notre approche", blocks: [
      p("{{name}} n'utilise pas de cookies de suivi. Le site et l'application s'appuient sur le stockage local de votre navigateur ou de votre appareil, uniquement pour faire fonctionner le service et mémoriser vos préférences."),
      box("success", "🛡", "Nous ne vendons ni ne partageons aucune donnée avec des courtiers en données ; nous ne lions pas vos données à des données de tiers à des fins de marketing ou de publicité ; nous n'utilisons aucun réseau publicitaire ciblé ni aucun suivi de vos activités sur d'autres applications ou sites."),
    ]},
    { title: "Ce que nous stockons", blocks: [
      table(["Élément", "Catégorie", "Durée", "Finalité"], [
        ["Jetons de connexion (stockage local)", "Essentiel", "Jusqu'à la déconnexion ou l'expiration de la session", "Vous maintenir connecté et sécuriser votre compte"],
        ["Préférences d'affichage : langue, thème (stockage local)", "Fonctionnel", "Jusqu'à l'effacement des données du navigateur", "Mémoriser vos réglages"],
        ["Choix de confidentialité (stockage local)", "Essentiel", "Jusqu'à l'effacement des données du navigateur", "Mémoriser vos choix du bandeau de préférences"],
        ["Code de parrainage (stockage local)", "Fonctionnel", "30 jours maximum, supprimé après l'inscription", "Attribuer un parrainage à votre inscription"],
        ["Identifiant de session de mesure (stockage de session)", "Mesure interne", "Jusqu'à la fermeture de l'onglet", "Statistiques d'usage anonymes envoyées à nos seuls serveurs (refusable)"],
        ["Vérification anti-robot Cloudflare Turnstile", "Sécurité", "Durée de la vérification", "Bloquer les robots à l'inscription et à la connexion ; signaux techniques uniquement, jamais utilisés pour la publicité"],
        ["Connexion avec Google (si vous l'utilisez)", "Authentification", "Le temps de la connexion", "Vous identifier via votre compte Google, selon la politique de Google"],
      ]),
    ]},
    { title: "Ce que nous n'utilisons pas", blocks: [
      ul([
        "Aucun cookie ni pixel publicitaire ou de ciblage.",
        "Aucun traceur de réseaux sociaux.",
        "Aucun outil d'analyse d'audience tiers : notre mesure d'usage est interne.",
        "Aucune liaison de vos données avec des données de tiers à des fins publicitaires ; aucune vente ni partage avec des courtiers en données.",
        "Aucun suivi de vos activités sur d'autres applications ou sites.",
      ]),
    ]},
    { title: "Application mobile", blocks: [
      p("Dans l'application, les affichages web et les requêtes réseau servent uniquement à des fonctionnalités essentielles : afficher les conditions d'utilisation et nous connecter à notre API. Aucun cookie de suivi n'est collecté ni utilisé."),
    ]},
    { title: "Mesure d'usage interne", blocks: [
      p("Pour améliorer le service, nous mesurons de façon anonyme les pages vues, les clics, les erreurs, le type d'appareil et le pays. Ces données sont envoyées uniquement à nos propres serveurs, ne sont partagées avec aucun tiers, ne sont pas croisées avec des données de tiers et ne servent jamais à la publicité."),
      p("Vous pouvez refuser cette mesure à tout moment via le bandeau de préférences (catégorie « Analytiques »)."),
    ]},
    { title: "Gérer vos choix", blocks: [
      ul([
        "Bandeau de préférences : accessible depuis le pied de page du site.",
        "Navigateur : vous pouvez effacer le stockage local à tout moment ; vous serez alors déconnecté et vos préférences seront réinitialisées.",
        "Compte : Paramètres → Confidentialité.",
      ]),
    ]},
    { title: "Contact", blocks: [
      contact([["Responsable", "{{name}}"], ["Email", "{{dpo}}"], ["Adresse postale", "{{address}}"]]),
      p("Pour en savoir plus sur le traitement de vos données, consultez l'onglet « Confidentialité »."),
    ]},
  ],
},

/* ═══ EN ═══ */
en: {
  toc: "Table of Contents",
  summaryTitle: "The essentials",
  summary: [
    "No tracking cookies and no advertising cookies.",
    "We only use your browser's local storage for essential functions (sign-in, language, theme, privacy choices).",
    "No data is sold or shared with data brokers or advertising networks.",
    "In the mobile app, web views serve essential functions only: no tracking cookie is collected there.",
  ],
  sections: [
    { title: "Our approach", blocks: [
      p("{{name}} does not use tracking cookies. The website and the app rely on your browser or device local storage, solely to run the service and remember your preferences."),
      box("success", "🛡", "We do not sell or share any data with data brokers; we do not link your data with third-party data for marketing or advertising purposes; we use no targeted advertising network and no tracking of your activity on other apps or websites."),
    ]},
    { title: "What we store", blocks: [
      table(["Item", "Category", "Duration", "Purpose"], [
        ["Sign-in tokens (local storage)", "Essential", "Until you sign out or the session expires", "Keep you signed in and secure your account"],
        ["Display preferences: language, theme (local storage)", "Functional", "Until you clear browser data", "Remember your settings"],
        ["Privacy choices (local storage)", "Essential", "Until you clear browser data", "Remember your choices from the preferences banner"],
        ["Referral code (local storage)", "Functional", "30 days at most, deleted after sign-up", "Attribute a referral to your registration"],
        ["Measurement session ID (session storage)", "Internal measurement", "Until the tab is closed", "Anonymous usage statistics sent to our own servers only (can be declined)"],
        ["Cloudflare Turnstile anti-bot check", "Security", "Length of the check", "Block bots at sign-up and login; technical signals only, never used for advertising"],
        ["Sign in with Google (if you use it)", "Authentication", "During sign-in", "Identify you through your Google account, under Google's policy"],
      ]),
    ]},
    { title: "What we do not use", blocks: [
      ul([
        "No advertising or targeting cookies or pixels.",
        "No social-media trackers.",
        "No third-party audience-analytics tool: our usage measurement is internal.",
        "No linking of your data with third-party data for advertising; no sale or sharing with data brokers.",
        "No tracking of your activity on other apps or websites.",
      ]),
    ]},
    { title: "Mobile app", blocks: [
      p("In the app, web views and network requests serve only essential functions: displaying the terms of use and connecting to our API. No tracking cookie is collected or used."),
    ]},
    { title: "Internal usage measurement", blocks: [
      p("To improve the service, we anonymously measure pages viewed, clicks, errors, device type and country. This data is sent only to our own servers, is shared with no third party, is not combined with third-party data and is never used for advertising."),
      p("You can decline this measurement at any time through the preferences banner (\"Analytics\" category)."),
    ]},
    { title: "Managing your choices", blocks: [
      ul([
        "Preferences banner: available from the site footer.",
        "Browser: you can clear local storage at any time; you will then be signed out and your preferences reset.",
        "Account: Settings → Privacy.",
      ]),
    ]},
    { title: "Contact", blocks: [
      contact([["Controller", "{{name}}"], ["Email", "{{dpo}}"], ["Postal address", "{{address}}"]]),
      p("To learn more about how your data is processed, see the \"Privacy\" tab."),
    ]},
  ],
},

/* ═══ ES ═══ */
es: {
  toc: "Índice",
  summaryTitle: "Lo esencial",
  summary: [
    "Sin cookies de seguimiento ni cookies publicitarias.",
    "Solo usamos el almacenamiento local de tu navegador para funciones esenciales (sesión, idioma, tema, opciones de privacidad).",
    "Ningún dato se vende ni se comparte con corredores de datos o redes publicitarias.",
    "En la aplicación móvil, las vistas web sirven solo para funciones esenciales: no se recopila ninguna cookie de seguimiento.",
  ],
  sections: [
    { title: "Nuestro enfoque", blocks: [
      p("{{name}} no utiliza cookies de seguimiento. El sitio y la aplicación se apoyan en el almacenamiento local de tu navegador o dispositivo, únicamente para que el servicio funcione y recordar tus preferencias."),
      box("success", "🛡", "No vendemos ni compartimos datos con corredores de datos; no vinculamos tus datos con datos de terceros con fines de marketing o publicidad; no usamos ninguna red publicitaria segmentada ni rastreamos tu actividad en otras aplicaciones o sitios web."),
    ]},
    { title: "Lo que almacenamos", blocks: [
      table(["Elemento", "Categoría", "Duración", "Finalidad"], [
        ["Tokens de sesión (almacenamiento local)", "Esencial", "Hasta cerrar sesión o hasta que expire la sesión", "Mantenerte conectado y proteger tu cuenta"],
        ["Preferencias de visualización: idioma, tema (almacenamiento local)", "Funcional", "Hasta borrar los datos del navegador", "Recordar tus ajustes"],
        ["Opciones de privacidad (almacenamiento local)", "Esencial", "Hasta borrar los datos del navegador", "Recordar tus elecciones del banner de preferencias"],
        ["Código de referido (almacenamiento local)", "Funcional", "30 días como máximo, se elimina tras el registro", "Atribuir un referido a tu registro"],
        ["ID de sesión de medición (almacenamiento de sesión)", "Medición interna", "Hasta cerrar la pestaña", "Estadísticas de uso anónimas enviadas solo a nuestros servidores (se pueden rechazar)"],
        ["Verificación antibots Cloudflare Turnstile", "Seguridad", "Duración de la verificación", "Bloquear bots en el registro y el inicio de sesión; solo señales técnicas, nunca para publicidad"],
        ["Inicio de sesión con Google (si lo usas)", "Autenticación", "Durante el inicio de sesión", "Identificarte mediante tu cuenta de Google, según la política de Google"],
      ]),
    ]},
    { title: "Lo que no utilizamos", blocks: [
      ul([
        "Ninguna cookie ni píxel publicitario o de segmentación.",
        "Ningún rastreador de redes sociales.",
        "Ninguna herramienta de análisis de audiencia de terceros: nuestra medición de uso es interna.",
        "Ninguna vinculación de tus datos con datos de terceros con fines publicitarios; ninguna venta ni intercambio con corredores de datos.",
        "Ningún seguimiento de tu actividad en otras aplicaciones o sitios web.",
      ]),
    ]},
    { title: "Aplicación móvil", blocks: [
      p("En la aplicación, las vistas web y las solicitudes de red sirven solo para funciones esenciales: mostrar las condiciones de uso y conectar con nuestra API. No se recopila ni se usa ninguna cookie de seguimiento."),
    ]},
    { title: "Medición de uso interna", blocks: [
      p("Para mejorar el servicio, medimos de forma anónima las páginas vistas, los clics, los errores, el tipo de dispositivo y el país. Estos datos se envían solo a nuestros propios servidores, no se comparten con ningún tercero, no se cruzan con datos de terceros y nunca se usan para publicidad."),
      p("Puedes rechazar esta medición en cualquier momento desde el banner de preferencias (categoría «Analíticas»)."),
    ]},
    { title: "Gestionar tus opciones", blocks: [
      ul([
        "Banner de preferencias: accesible desde el pie de página del sitio.",
        "Navegador: puedes borrar el almacenamiento local en cualquier momento; se cerrará tu sesión y se restablecerán tus preferencias.",
        "Cuenta: Ajustes → Privacidad.",
      ]),
    ]},
    { title: "Contacto", blocks: [
      contact([["Responsable", "{{name}}"], ["Correo", "{{dpo}}"], ["Dirección postal", "{{address}}"]]),
      p("Para saber más sobre el tratamiento de tus datos, consulta la pestaña «Privacidad»."),
    ]},
  ],
},

/* ═══ DE ═══ */
de: {
  toc: "Inhaltsverzeichnis",
  summaryTitle: "Das Wichtigste",
  summary: [
    "Keine Tracking-Cookies und keine Werbe-Cookies.",
    "Wir nutzen nur den lokalen Speicher Ihres Browsers für wesentliche Funktionen (Anmeldung, Sprache, Design, Datenschutzentscheidungen).",
    "Keine Daten werden verkauft oder mit Datenhändlern oder Werbenetzwerken geteilt.",
    "In der mobilen App dienen Webansichten nur wesentlichen Funktionen: Es werden dort keine Tracking-Cookies erhoben.",
  ],
  sections: [
    { title: "Unser Ansatz", blocks: [
      p("{{name}} verwendet keine Tracking-Cookies. Website und App nutzen den lokalen Speicher Ihres Browsers oder Geräts ausschließlich, um den Dienst zu betreiben und Ihre Einstellungen zu merken."),
      box("success", "🛡", "Wir verkaufen oder teilen keine Daten mit Datenhändlern; wir verknüpfen Ihre Daten nicht mit Daten Dritter für Marketing- oder Werbezwecke; wir nutzen kein gezieltes Werbenetzwerk und verfolgen Ihre Aktivitäten nicht in anderen Apps oder auf anderen Websites."),
    ]},
    { title: "Was wir speichern", blocks: [
      table(["Element", "Kategorie", "Dauer", "Zweck"], [
        ["Anmelde-Token (lokaler Speicher)", "Wesentlich", "Bis zur Abmeldung oder zum Ablauf der Sitzung", "Sie angemeldet halten und Ihr Konto schützen"],
        ["Anzeigeeinstellungen: Sprache, Design (lokaler Speicher)", "Funktional", "Bis zum Löschen der Browserdaten", "Ihre Einstellungen merken"],
        ["Datenschutzentscheidungen (lokaler Speicher)", "Wesentlich", "Bis zum Löschen der Browserdaten", "Ihre Auswahl im Einstellungsbanner merken"],
        ["Empfehlungscode (lokaler Speicher)", "Funktional", "Höchstens 30 Tage, nach der Registrierung gelöscht", "Eine Empfehlung Ihrer Registrierung zuordnen"],
        ["Mess-Sitzungs-ID (Sitzungsspeicher)", "Interne Messung", "Bis zum Schließen des Tabs", "Anonyme Nutzungsstatistiken nur an unsere eigenen Server (ablehnbar)"],
        ["Cloudflare-Turnstile-Bot-Prüfung", "Sicherheit", "Dauer der Prüfung", "Bots bei Registrierung und Anmeldung blockieren; nur technische Signale, nie für Werbung"],
        ["Anmeldung mit Google (falls genutzt)", "Authentifizierung", "Während der Anmeldung", "Identifizierung über Ihr Google-Konto gemäß der Richtlinie von Google"],
      ]),
    ]},
    { title: "Was wir nicht verwenden", blocks: [
      ul([
        "Keine Werbe- oder Targeting-Cookies oder -Pixel.",
        "Keine Social-Media-Tracker.",
        "Kein Reichweitenanalyse-Tool von Drittanbietern: Unsere Nutzungsmessung ist intern.",
        "Keine Verknüpfung Ihrer Daten mit Daten Dritter für Werbung; kein Verkauf und keine Weitergabe an Datenhändler.",
        "Keine Verfolgung Ihrer Aktivitäten in anderen Apps oder auf anderen Websites.",
      ]),
    ]},
    { title: "Mobile App", blocks: [
      p("In der App dienen Webansichten und Netzwerkanfragen nur wesentlichen Funktionen: Anzeige der Nutzungsbedingungen und Verbindung zu unserer API. Es werden keine Tracking-Cookies erhoben oder verwendet."),
    ]},
    { title: "Interne Nutzungsmessung", blocks: [
      p("Zur Verbesserung des Dienstes messen wir anonym aufgerufene Seiten, Klicks, Fehler, Gerätetyp und Land. Diese Daten gehen nur an unsere eigenen Server, werden nicht an Dritte weitergegeben, nicht mit Daten Dritter verknüpft und nie für Werbung genutzt."),
      p("Sie können diese Messung jederzeit über das Einstellungsbanner ablehnen (Kategorie „Analyse“)."),
    ]},
    { title: "Ihre Auswahl verwalten", blocks: [
      ul([
        "Einstellungsbanner: erreichbar über die Fußzeile der Website.",
        "Browser: Sie können den lokalen Speicher jederzeit löschen; Sie werden dann abgemeldet und Ihre Einstellungen zurückgesetzt.",
        "Konto: Einstellungen → Datenschutz.",
      ]),
    ]},
    { title: "Kontakt", blocks: [
      contact([["Verantwortlicher", "{{name}}"], ["E-Mail", "{{dpo}}"], ["Postanschrift", "{{address}}"]]),
      p("Mehr zur Verarbeitung Ihrer Daten finden Sie im Reiter „Datenschutz“."),
    ]},
  ],
},

/* ═══ PT ═══ */
pt: {
  toc: "Índice",
  summaryTitle: "O essencial",
  summary: [
    "Sem cookies de rastreamento nem cookies publicitários.",
    "Usamos apenas o armazenamento local do seu navegador para funções essenciais (sessão, idioma, tema, escolhas de privacidade).",
    "Nenhum dado é vendido nem partilhado com corretores de dados ou redes publicitárias.",
    "Na aplicação móvel, as vistas web servem apenas funções essenciais: nenhum cookie de rastreamento é recolhido.",
  ],
  sections: [
    { title: "A nossa abordagem", blocks: [
      p("A {{name}} não utiliza cookies de rastreamento. O site e a aplicação recorrem ao armazenamento local do seu navegador ou dispositivo, apenas para fazer o serviço funcionar e lembrar as suas preferências."),
      box("success", "🛡", "Não vendemos nem partilhamos dados com corretores de dados; não associamos os seus dados a dados de terceiros para fins de marketing ou publicidade; não usamos nenhuma rede publicitária segmentada nem seguimos a sua atividade noutras aplicações ou sites."),
    ]},
    { title: "O que armazenamos", blocks: [
      table(["Elemento", "Categoria", "Duração", "Finalidade"], [
        ["Tokens de sessão (armazenamento local)", "Essencial", "Até terminar a sessão ou esta expirar", "Mantê-lo ligado e proteger a sua conta"],
        ["Preferências de apresentação: idioma, tema (armazenamento local)", "Funcional", "Até apagar os dados do navegador", "Lembrar as suas definições"],
        ["Escolhas de privacidade (armazenamento local)", "Essencial", "Até apagar os dados do navegador", "Lembrar as suas escolhas do banner de preferências"],
        ["Código de indicação (armazenamento local)", "Funcional", "30 dias no máximo, eliminado após o registo", "Atribuir uma indicação ao seu registo"],
        ["ID de sessão de medição (armazenamento de sessão)", "Medição interna", "Até fechar o separador", "Estatísticas de utilização anónimas enviadas apenas para os nossos servidores (recusável)"],
        ["Verificação anti-bot Cloudflare Turnstile", "Segurança", "Duração da verificação", "Bloquear bots no registo e no início de sessão; apenas sinais técnicos, nunca para publicidade"],
        ["Início de sessão com Google (se o usar)", "Autenticação", "Durante o início de sessão", "Identificá-lo através da sua conta Google, segundo a política da Google"],
      ]),
    ]},
    { title: "O que não utilizamos", blocks: [
      ul([
        "Nenhum cookie ou pixel publicitário ou de segmentação.",
        "Nenhum rastreador de redes sociais.",
        "Nenhuma ferramenta de análise de audiência de terceiros: a nossa medição de utilização é interna.",
        "Nenhuma associação dos seus dados a dados de terceiros para publicidade; nenhuma venda ou partilha com corretores de dados.",
        "Nenhum rastreamento da sua atividade noutras aplicações ou sites.",
      ]),
    ]},
    { title: "Aplicação móvel", blocks: [
      p("Na aplicação, as vistas web e os pedidos de rede servem apenas funcionalidades essenciais: apresentar as condições de utilização e ligar à nossa API. Nenhum cookie de rastreamento é recolhido ou utilizado."),
    ]},
    { title: "Medição de utilização interna", blocks: [
      p("Para melhorar o serviço, medimos de forma anónima as páginas vistas, os cliques, os erros, o tipo de dispositivo e o país. Estes dados são enviados apenas para os nossos próprios servidores, não são partilhados com terceiros, não são cruzados com dados de terceiros e nunca são usados para publicidade."),
      p("Pode recusar esta medição a qualquer momento no banner de preferências (categoria «Analíticos»)."),
    ]},
    { title: "Gerir as suas escolhas", blocks: [
      ul([
        "Banner de preferências: acessível a partir do rodapé do site.",
        "Navegador: pode apagar o armazenamento local a qualquer momento; terminará a sessão e as suas preferências serão repostas.",
        "Conta: Definições → Privacidade.",
      ]),
    ]},
    { title: "Contacto", blocks: [
      contact([["Responsável", "{{name}}"], ["E-mail", "{{dpo}}"], ["Morada postal", "{{address}}"]]),
      p("Para saber mais sobre o tratamento dos seus dados, consulte o separador «Privacidade»."),
    ]},
  ],
},

/* ═══ AR ═══ */
ar: {
  toc: "جدول المحتويات",
  summaryTitle: "الخلاصة",
  summary: [
    "لا ملفات تعريف ارتباط للتتبع ولا ملفات إعلانية.",
    "نستخدم فقط التخزين المحلي في متصفحك لوظائف أساسية (تسجيل الدخول، اللغة، المظهر، خيارات الخصوصية).",
    "لا تُباع أي بيانات ولا تُشارك مع سماسرة البيانات أو الشبكات الإعلانية.",
    "في تطبيق الهاتف، تُستخدم عروض الويب لوظائف أساسية فقط: لا تُجمع أي ملفات تعريف ارتباط للتتبع.",
  ],
  sections: [
    { title: "نهجنا", blocks: [
      p("لا تستخدم {{name}} ملفات تعريف ارتباط للتتبع. يعتمد الموقع والتطبيق على التخزين المحلي في متصفحك أو جهازك، فقط لتشغيل الخدمة وتذكّر تفضيلاتك."),
      box("success", "🛡", "لا نبيع ولا نشارك أي بيانات مع سماسرة البيانات؛ ولا نربط بياناتك ببيانات جهات خارجية لأغراض التسويق أو الإعلان؛ ولا نستخدم أي شبكة إعلانية موجّهة ولا نتتبع نشاطك عبر تطبيقات أو مواقع أخرى."),
    ]},
    { title: "ما نقوم بتخزينه", blocks: [
      table(["العنصر", "الفئة", "المدة", "الغرض"], [
        ["رموز تسجيل الدخول (التخزين المحلي)", "أساسي", "حتى تسجيل الخروج أو انتهاء الجلسة", "إبقاؤك مسجّلًا للدخول وحماية حسابك"],
        ["تفضيلات العرض: اللغة والمظهر (التخزين المحلي)", "وظيفي", "حتى مسح بيانات المتصفح", "تذكّر إعداداتك"],
        ["خيارات الخصوصية (التخزين المحلي)", "أساسي", "حتى مسح بيانات المتصفح", "تذكّر اختياراتك من شريط التفضيلات"],
        ["رمز الإحالة (التخزين المحلي)", "وظيفي", "30 يومًا كحد أقصى، ويُحذف بعد التسجيل", "نسب الإحالة إلى تسجيلك"],
        ["معرّف جلسة القياس (تخزين الجلسة)", "قياس داخلي", "حتى إغلاق التبويب", "إحصاءات استخدام مجهولة تُرسل إلى خوادمنا فقط (يمكن رفضها)"],
        ["التحقق ضد الروبوتات Cloudflare Turnstile", "أمان", "مدة التحقق", "منع الروبوتات عند التسجيل وتسجيل الدخول؛ إشارات تقنية فقط ولا تُستخدم للإعلان"],
        ["تسجيل الدخول عبر Google (إن استخدمته)", "مصادقة", "أثناء تسجيل الدخول", "التعرف عليك عبر حساب Google وفق سياسة Google"],
      ]),
    ]},
    { title: "ما لا نستخدمه", blocks: [
      ul([
        "لا ملفات تعريف ارتباط أو بكسلات إعلانية أو استهدافية.",
        "لا أدوات تتبع لوسائل التواصل الاجتماعي.",
        "لا أداة تحليل جمهور من جهة خارجية: قياس الاستخدام لدينا داخلي.",
        "لا ربط لبياناتك ببيانات جهات خارجية لأغراض الإعلان؛ ولا بيع أو مشاركة مع سماسرة البيانات.",
        "لا تتبع لنشاطك عبر تطبيقات أو مواقع أخرى.",
      ]),
    ]},
    { title: "تطبيق الهاتف", blocks: [
      p("في التطبيق، تُستخدم عروض الويب وطلبات الشبكة لوظائف أساسية فقط: عرض شروط الاستخدام والاتصال بواجهتنا البرمجية. ولا تُجمع أو تُستخدم أي ملفات تعريف ارتباط للتتبع."),
    ]},
    { title: "قياس الاستخدام الداخلي", blocks: [
      p("لتحسين الخدمة، نقيس بشكل مجهول الصفحات المعروضة والنقرات والأخطاء ونوع الجهاز والبلد. تُرسل هذه البيانات إلى خوادمنا فقط، ولا تُشارك مع أي طرف ثالث، ولا تُدمج مع بيانات جهات خارجية، ولا تُستخدم أبدًا للإعلان."),
      p("يمكنك رفض هذا القياس في أي وقت من شريط التفضيلات (فئة «التحليلية»)."),
    ]},
    { title: "إدارة اختياراتك", blocks: [
      ul([
        "شريط التفضيلات: متاح من أسفل الموقع.",
        "المتصفح: يمكنك مسح التخزين المحلي في أي وقت؛ وسيتم تسجيل خروجك وإعادة ضبط تفضيلاتك.",
        "الحساب: الإعدادات ← الخصوصية.",
      ]),
    ]},
    { title: "الاتصال", blocks: [
      contact([["المسؤول", "{{name}}"], ["البريد الإلكتروني", "{{dpo}}"], ["العنوان البريدي", "{{address}}"]]),
      p("لمعرفة المزيد عن معالجة بياناتك، راجع تبويب «الخصوصية»."),
    ]},
  ],
},
};

export default COOKIES;